import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { invalidateReportsCache } from "@/lib/react-query/invalidateReportsCache";

export type ApplicationOperationalStatus =
  | "not_notified"
  | "notified"
  | "scheduled"
  | "attended"
  | "no_response"
  | "blocked";

export type ApplicationContactChannel =
  | "meta_cloud_api"
  | "whatsapp_web"
  | "manual";

export type ApplicationBlockedReason =
  | "missing_phone"
  | "invalid_phone"
  | "opt_out"
  | "technical_failure"
  | "deceased_pet"
  | "owner_not_identified"
  | "other";

export type ApplicationAppointmentMatchSource =
  | "linked"
  | "pet_window";

export type ApplicationAppointmentType =
  | "consulta"
  | "cirurgia"
  | "banho_tosa"
  | "retorno"
  | "vacina"
  | "aplicacao"
  | "outro";

export type ApplicationOperationsRow = {
  pet_application_id: string | null;
  pet_application_plan_id: string | null;
  clinic_id: string;
  pet_id: string | null;
  owner_id: string | null;
  item_id: string | null;
  application_date: string | null;
  dose_number: number | null;
  due_date: string | null;
  reference_date: string | null;
  days_until_due: number | null;
  days_overdue: number | null;
  is_overdue: boolean;
  overdue_bucket: string | null;
  operational_bucket: "pending" | "notified" | "scheduled" | "overdue";
  overdue_reason: "dose" | "appointment" | null;
  has_active_appointment: boolean;
  appointment_is_overdue: boolean;
  has_phone_number: boolean;
  current_operational_status: ApplicationOperationalStatus;
  view_bucket: "pending" | "notified" | "scheduled" | "overdue";
  appointment_id: string | null;
  appointment_status: string | null;
  appointment_starts_at: string | null;
  appointment_type: ApplicationAppointmentType | null;
  appointment_match_source: ApplicationAppointmentMatchSource | null;
  last_dispatch_status: string | null;
  last_contact_channel: ApplicationContactChannel | null;
  last_contact_source: "automatic" | "manual" | null;
  last_contact_at: string | null;
  last_contact_by: string | null;
  last_contact_result: string | null;
  blocked_reason: ApplicationBlockedReason | null;
  notes: string | null;
  has_valid_contact: boolean;
  has_whatsapp_channel: boolean;
  has_cloud_api_tracking: boolean;
  preferred_phone: string | null;
  owner_whatsapp_wa_id: string | null;
  owner_whatsapp_user_id: string | null;
  owner_whatsapp_parent_user_id: string | null;
  owner_whatsapp_username: string | null;
  pet_name: string | null;
  pet_record_number: string | null;
  owner_name: string | null;
  item_name: string | null;
  subtype_name: string | null;
  species_name: string | null;
  breed_name: string | null;
  sex_name: string | null;
  birth_date: string | null;
  current_weight: number | null;
  death_date: string | null;
};

export type ApplicationAnalyticsRow = {
  application_id: string;
  clinic_id: string;
  pet_id: string | null;
  owner_id: string | null;
  medical_record_id: string | null;
  application_date: string;
  dose_number: number | null;
  item_id: string | null;
  item_name: string | null;
  subtype_name: string | null;
  pet_name: string | null;
  pet_record_number: string | null;
  owner_name: string | null;
  species_name: string | null;
  breed_name: string | null;
  sex_name: string | null;
  birth_date: string | null;
  current_weight: number | null;
  professional_name: string | null;
  consultation_date: string | null;
  notes: string | null;
};

export type ApplicationTrackingMutationInput = {
  clinicId: string;
  petApplicationId?: string | null;
  petApplicationPlanId?: string | null;
  dueDate: string;
  operationalStatus?: ApplicationOperationalStatus | null;
  lastContactChannel?: ApplicationContactChannel | null;
  lastContactSource?: "automatic" | "manual" | null;
  lastContactAt?: string | null;
  lastContactResult?: string | null;
  blockedReason?: ApplicationBlockedReason | null;
  notes?: string | null;
};

type RpcClient = {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message?: string } | null }>;
};

const supabaseRpc = supabase as unknown as RpcClient;

export function useApplicationOperations(
  clinicId?: string,
  operationalBucket?: "pending" | "notified" | "scheduled" | "overdue",
  startDate?: string,
  endDate?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ["application-operations", clinicId, operationalBucket, startDate, endDate],
    enabled: Boolean(clinicId) && enabled,
    staleTime: 0,
    gcTime: 60_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<ApplicationOperationsRow[]> => {
      const { data, error } = await supabaseRpc.rpc("get_application_operations_rows", {
        p_clinic_id: clinicId,
        p_operational_bucket: operationalBucket ?? null,
        p_start_date: startDate ?? null,
        p_end_date: endDate ?? null,
      });

      if (error) throw error;
      return (data ?? []) as ApplicationOperationsRow[];
    },
  });
}

export function useApplicationAnalytics(
  clinicId?: string,
  startDate?: string,
  endDate?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ["application-analytics", clinicId, startDate, endDate],
    enabled: Boolean(clinicId) && enabled,
    staleTime: 0,
    gcTime: 60_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<ApplicationAnalyticsRow[]> => {
      const { data, error } = await supabaseRpc.rpc("get_application_analytics_rows", {
        p_clinic_id: clinicId,
        p_start_at: startDate ? `${startDate.slice(0, 10)}T00:00:00` : null,
        p_end_at: endDate ? `${endDate.slice(0, 10)}T23:59:59.999` : null,
      });

      if (error) throw error;
      return (data ?? []) as ApplicationAnalyticsRow[];
    },
  });
}

export function useUpsertApplicationTracking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ApplicationTrackingMutationInput) => {
      const dueDate = input.dueDate.length >= 10 ? input.dueDate.slice(0, 10) : input.dueDate;

      const { data, error } = await supabaseRpc.rpc("upsert_application_follow_up_tracking", {
        p_clinic_id: input.clinicId,
        p_pet_application_id: input.petApplicationId ?? null,
        p_pet_application_plan_id: input.petApplicationPlanId ?? null,
        p_due_date: dueDate,
        p_operational_status: input.operationalStatus ?? null,
        p_last_contact_channel: input.lastContactChannel ?? null,
        p_last_contact_source: input.lastContactSource ?? null,
        p_last_contact_at: input.lastContactAt ?? null,
        p_last_contact_result: input.lastContactResult ?? null,
        p_blocked_reason: input.blockedReason ?? null,
        p_notes: input.notes ?? null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["application-operations", variables.clinicId] });
      queryClient.removeQueries({ queryKey: ["application-operations", variables.clinicId] });
      queryClient.invalidateQueries({ queryKey: ["application-analytics", variables.clinicId] });
      queryClient.removeQueries({ queryKey: ["application-analytics", variables.clinicId] });
      queryClient.invalidateQueries({ queryKey: ["applications-with-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-dose-state"] });
      invalidateReportsCache(queryClient);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Unexpected error";
      toast.error(message);
    },
  });
}
